import api from '@/lib/services/general.service'
import { toast } from 'vue3-toastify';
import { loginForSave } from '@/lib/services/guest'
import { canCreateAsGuest, addGuestDraft } from '@/lib/services/guestDrafts'
import { dateOnlyDay, taskDueInstant } from '@/lib/taskDue'


const initialState = () => ({
  tasks: {
    items: [],
    index: 0,
    totalCount: 0,
    totalPages: 0,
    hasPreviousPage: 0,
    hasNextPage: 0,
  },
  isLoading: true,
  taskCounts: {
    completedCount: 0,
    importantCount: 0,
    monthCount: 0,
    somedayCount: 0,
    todayCount: 0,
    totalCount: 0,
    waitingOnCount: 0,
    weekCount: 0,
    binCount: 0,
  },
});
export const state = initialState();

export const getters = {
  tasks: s => s.tasks,

  importantTasks: (state) => {
    let items = state.tasks.items;
    return items
      .filter(task => task.isImportant && task.status !== 'done')
      .sort((a, b) => new Date(a.dueDate) - new Date(b.dueDate));
  },
  waitingOnTasks: (state) => {
    let items = state.tasks.items;
    return items
      .filter(task => task.status === "waitingon")
      .sort((a, b) => new Date(a.dueDate) - new Date(b.dueDate));
  },
  doneTasks: (state) => {
    let items = state.tasks.items;
    return items
      .filter(task => task.status === "done")
      .sort((a, b) => new Date(a.dueDate) - new Date(b.dueDate));
  },
  somedayTasks: (state) => {
    let items = state.tasks.items;
    return items
      .filter(task => task.status === "someday")
      .sort((a, b) => new Date(a.dueDate) - new Date(b.dueDate));
  },
  unscheduledTasks: (state) => {
    let items = state.tasks.items;
    return items
      .filter(task => task.dueDate == null && task.status !== 'someday' && task.status !== 'done')
      .sort((a, b) => new Date(a.dueDate) - new Date(b.dueDate));
  },
  todayTasks: (state) => {
    const currentDate = new Date();
    let items = state.tasks.items;
    return items
      .filter(task => {
        const taskDueDate = new Date(task.dueDate);
        // "Still today": a timed task until its time, a date-only task until the day ends
        // (lib/taskDue.js) — it used to fall out of Today the moment its midnight had passed.
        return taskDueDate.toDateString() === currentDate.toDateString() &&
          taskDueInstant(task) >= currentDate && task.status !== 'done'
      })
      .sort((a, b) => new Date(a.dueDate) - new Date(b.dueDate));
  },
  tasksWithDate: (state) => {
    const currentDate = new Date();
    const currentDateOnly = new Date(currentDate.getFullYear(), currentDate.getMonth(), currentDate.getDate());
    let items = state.tasks.items;
    return items
      .filter(task => {
        const taskDueDate = new Date(task.dueDate);
        let taskDueDateOnly = new Date(taskDueDate.getFullYear(), taskDueDate.getMonth(), taskDueDate.getDate());
        return taskDueDateOnly >= currentDateOnly && task.status !== 'done';
      })
      .sort((a, b) => new Date(a.dueDate) - new Date(b.dueDate));
  },
  overdueTasks: (state) => {
    let items = state.tasks.items;
    const currentDate = new Date();
    return items
      .filter(task => {
        // A date-only task is overdue once its DAY is over (lib/taskDue.js), not from 00:00.
        const taskDueDate = taskDueInstant(task);
        return taskDueDate != null && task.status !== 'someday' && task.status !== 'done' && taskDueDate < currentDate;
      })
      .sort((a, b) => new Date(b.dueDate) - new Date(a.dueDate));
  },
  nextWeekTasks: (state) => {
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate()); // start of today (00:00)
    const next7DaysEndDate = new Date(today);
    next7DaysEndDate.setDate(today.getDate() + 7);

    return state.tasks.items
      .filter(task => {
        const taskDueDate = new Date(task.dueDate);
        const taskDateOnly = new Date(taskDueDate.getFullYear(), taskDueDate.getMonth(), taskDueDate.getDate());

        return (
          taskDateOnly > today && // strictly after today
          taskDateOnly <= next7DaysEndDate &&
          task.status !== 'done'
        );
      })
      .sort((a, b) => new Date(a.dueDate) - new Date(b.dueDate));
  },
  nextMonthTasks: (state) => {
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate()); // start of today
    const next30DaysEndDate = new Date(today);
    next30DaysEndDate.setDate(today.getDate() + 30);

    return state.tasks.items
      .filter(task => {
        const taskDueDate = new Date(task.dueDate);
        const taskDateOnly = new Date(taskDueDate.getFullYear(), taskDueDate.getMonth(), taskDueDate.getDate());

        return (
          taskDateOnly > today && // exclude today
          taskDateOnly <= next30DaysEndDate &&
          task.status !== 'done'
        );
      })
      .sort((a, b) => new Date(a.dueDate) - new Date(b.dueDate));
  },
  binTasks: (state) => {
    let items = state.tasks.items;
    return items
      .filter(task => task.isDeleted)
      .sort((a, b) => new Date(b.updateDate || b.createDate) - new Date(a.updateDate || a.createDate));
  },
  isLoading: s => s.isLoading,
  taskItems: s => s.taskItems,
  taskCounts: s => s.taskCounts
};
export const actions = {
  async fetchTasks({ commit, dispatch }, requestData) {
    // The listing's refresh spins on this flag (Pouria, 2026-08-30: "the refresh btn is not
    // spinning") — it was set once at boot and never again.
    commit('SET_LOADING', true)
    return await api.get(`task`, requestData).then(response => {
      if (response) {
        const items = response.data.items.map(item => {
          if (item.dueDate != null && item.isAllDay) {
            // A DATE-ONLY task (lib/taskDue.js): the stored UTC-midnight day, re-anchored to the
            // viewer's local midnight — never put through the offset shift below, which would move
            // it a day for anyone west of UTC.
            const day = dateOnlyDay(item.dueDate)
            item.dueDate = day ? day.toISOString() : item.dueDate
          } else if (item.dueDate != null) {
            const dueDate = new Date(item.dueDate)
            const localDueDate = new Date(dueDate.getTime() - (dueDate.getTimezoneOffset() * 60000))
            item.dueDate = localDueDate.toISOString()
          }
          if (item.remindDate != null) {
            const remindDate = new Date(item.remindDate)
            const localRemindDate = new Date(remindDate.getTime() - (remindDate.getTimezoneOffset() * 60000))
            item.remindDate = localRemindDate.toISOString()
          }
          return item;
        })

        response.data.items = items
        commit("SET_TASKS", response.data)
        dispatch('fetchTaskCount');
      }
      return true;
    }).finally(() => commit('SET_LOADING', false));
  },
  async addTask({ commit, dispatch, rootGetters }, task) {
    // Guests keep up to GUEST_LIMIT tasks locally; the next one requires signing in.
    if (!rootGetters['user/isAuthenticated']) {
      if (!canCreateAsGuest('task')) { loginForSave(); return new Promise(() => {}) }
      const payload = { ...task }; delete payload.silentToast
      const id = addGuestDraft('task', payload)
      const now = new Date().toISOString()
      const local = { ...payload, id, _local: true, createdDate: now, updateDate: now }
      commit('ADD_TASK', local)
      return local
    }
    const silent = !!task.silentToast
    delete task.silentToast
    const toastId = silent ? null : toast.loading(
      'Please wait...',
      {
        theme: 'task',
      }
    );
    try {
      const response = await api.insert('task', task);
      if (response) {
        if (task.timelineId) {
          dispatch('timeline/addTaskIntoTimeline', response.data, { root: true })
          // dispatch('sidepanel/addTaskIntoTimeline', response.data, { root: true })
        }
        commit("ADD_TASK", response.data);
        if (!silent) setTimeout(() => {
          toast.update(toastId, {
            render: 'Task added',
            autoClose: true,
            closeOnClick: true,
            closeButton: true,
            theme: 'task',
            isLoading: false,
          });
          toast.done(toastId);
        });
        dispatch('fetchTaskCount');
        // Return the created task (id + timelineId) so callers can attach documents to it.
        // Still truthy, so existing `if (await addTask(...))` checks keep working.
        return response.data;
      }
      return false;

    } catch (error) {
      console.error('Error adding task:', error);
      return false;
    }
  },
  async fetchTaskCount({ commit }) {
    return await api.get(`task/count`, {}).then(response => {
      if (response) {
        commit("SET_COUNT", response.data);
      }
      return true;
    });
  },
  // ONE task by id — same reason as note/fetchNoteById: the SmartBox edits a task opened from a
  // story step, where no task list has been loaded to read its body from.
  async fetchTaskById(_ctx, id) {
    if (!id) return null;
    try {
      const response = await api.getById('task', id);
      return response?.data?.data ?? response?.data ?? null;
    } catch (error) {
      console.error('Error fetching task:', error);
      return null;
    }
  },
  async updateStatus({ commit, dispatch }, request) {
    // `silent` (2026-09-15 parity audit B-5, busy-my-web#466): the universal box already says
    // "Completed" in the opened task's own header the instant the tick is pressed (#415), so the
    // global "Task marked as completed" toast beside it was the same answer twice. It rides on the
    // request and is stripped before the call — the endpoint takes `{ ids, status }` only.
    const { silent, ...body } = request || {}
    const toastId = silent ? null : toast.loading(
      'Please wait...',
      {
        theme: 'task',
      }
    );
    return api.insert(`task/updateStatus`, body)
      .then((response) => {
        if (response) {
          response.data.forEach(item => {
            if (item.timelineId) {
              dispatch('timeline/editTaskTimeline', item, { root: true });
              dispatch('sidepanel/editTaskTimeline', item, { root: true });
            }
          });
          // commit("UPDATE_TASK", response.data);
          const statusMessageMap = {
            2: 'Task marked as completed',
            4: 'Task marked as waiting on',
            'waitingon': 'Task marked as waiting on',
            1: 'Task marked as uncompleted',
            'none': 'Task status cleared',
          }
          const statusMessage = statusMessageMap[body.status] ?? 'Task updated'
          if (!silent) setTimeout(() => {
            toast.update(toastId, {
              render: statusMessage,
              autoClose: true,
              closeOnClick: true,
              closeButton: true,
              theme: 'task',
              isLoading: false,
            });
            toast.done(toastId);
          });
          dispatch('fetchTaskCount');
          dispatch('fetchTasks', {
            page: 1,
            pageSize: 10000,
            order: 1,
            search: ''
          });
          return true;
        }
        return false;
      });
  },
  async markAsImportant({ commit, dispatch, state }, request) {
    const toastId = toast.loading(
      'Please wait...',
      {
        theme: 'task',
      }
    );
    let ids;
    if (Array.isArray(request) && request.length === 1) {
      ids = [request[0]];
    } else {
      ids = calculateForMarkAsImportant(state, request).ids;
    }
    const count = ids.length;
    let message = count === 1 ? 'Task sets as important' : `${count} tasks sets as important`;
    if (count == 0) {
      message = request.length === 1
        ? 'Task already important'
        : `${request.length} tasks already important`;
      setTimeout(() => {
        toast.update(toastId, {
          render: message,
          autoClose: 1000,
          closeOnClick: true,
          closeButton: true,
          theme: 'task',
          isLoading: false,
        });
        toast.done(toastId);
      });
      await new Promise(resolve => setTimeout(resolve, 1000));
      return true;
    } else {
      return api.insert(`task/markAsImportant`, { ids })
        .then((response) => {
          if (response) {
            response.data.forEach(item => {
              if (item.timelineId) {
                dispatch('timeline/editTaskTimeline', item, { root: true });
                dispatch('sidepanel/editTaskTimeline', item, { root: true });
              }
            });
            setTimeout(() => {
              toast.update(toastId, {
                render: message,
                autoClose: 1000,
                closeOnClick: true,
                closeButton: true,
                theme: 'task',
                isLoading: false,
              });
              toast.done(toastId);
            });
            dispatch('fetchTaskCount');
            dispatch('fetchTasks', {
              page: 1,
              pageSize: 10000,
              order: 1,
              search: ''
            });
            return true;
          }
          return false;
        });
    }



  },
  async markAsNotImportant({ commit, dispatch, state }, request) {
    const toastId = toast.loading(
      'Please wait...',
      {
        theme: 'task',
      }
    );
    let ids;
    if (Array.isArray(request) && request.length === 1) {
      ids = [request[0]];
    } else {
      ids = calculateForMarkAsNotImportant(state, request).ids;
    }
    const count = ids.length;
    let message = count === 1 ? 'Task sets as not important' : `${count} tasks sets as not important`;
    if (count == 0) {
      message = request.length === 1
        ? 'Task already not important'
        : `${request.length} tasks already not important`;
      setTimeout(() => {
        toast.update(toastId, {
          render: message,
          autoClose: 1000,
          closeOnClick: true,
          closeButton: true,
          theme: 'task',
          isLoading: false,
        });
        toast.done(toastId);
      });
      await new Promise(resolve => setTimeout(resolve, 1000));
      return true;
    }
    else {
      return api.insert(`task/markAsNotImportant`, { ids })
        .then((response) => {
          if (response) {
            response.data.forEach(item => {
              if (item.timelineId) {
                dispatch('timeline/editTaskTimeline', item, { root: true });
                dispatch('sidepanel/editTaskTimeline', item, { root: true });
              }
            });
            setTimeout(() => {
              toast.update(toastId, {
                render: message,
                autoClose: true,
                closeOnClick: true,
                closeButton: true,
                theme: 'task',
                isLoading: false,
              });
              toast.done(toastId);
            });
            dispatch('fetchTaskCount');
            dispatch('fetchTasks', {
              page: 1,
              pageSize: 10000,
              order: 1,
              search: ''
            });
            return true;
          }
          return false;
        });
    }

  },
  async deleteTask({ commit, dispatch }, request) {
    // `request` may be the bare id array it always was, or `{ ids, silent }` (2026-09-15 parity
    // audit B-5, busy-my-web#466): the universal box reports a delete in its own pink strip with
    // Undo, and the global toast that used to fire alongside it was the second notification the
    // audit reports for one action. Silent is the BOX's choice — every list page still toasts.
    const ids = Array.isArray(request) ? request : (request?.ids || [])
    const silent = !Array.isArray(request) && !!request?.silent
    const count = ids.length
    let message = ''
    const toastId = silent ? null : toast.loading(
      'Please wait...',
      {
        theme: 'task',
      }
    );
    if (count === 1) {
      message = 'Task deleted'
    } else {
      message = count + ' tasks deleted'
    }
    return await api.deleteWithRequest(`task/delete`, { ids })
      .then((response) => {
        if (response.data) {
          commit("REMOVE_TASK", ids);
          // Deleting the last entity out of an ongoing empties (hides) it server-side — re-read the
          // ongoing list so the sidebar drops it without a manual refresh. Restoring brings it back.
          dispatch('ongoing/syncAfterEntityChange', null, { root: true });
          if (!silent) setTimeout(() => {
            toast.update(toastId, {
              render: message,
              autoClose: true,
              closeOnClick: true,
              closeButton: true,
              theme: 'task',
              isLoading: false,
            });
            toast.done(toastId);
          });
          dispatch('fetchTaskCount');
          return true;
        }
        return false;
      });
  },
  async restoreTasks({ dispatch }, ids) {
    const count = ids.length;
    const message = count === 1 ? 'Task restored' : `${count} tasks restored`;
    const toastId = toast.loading('Please wait...', { theme: 'task' });
    try {
      const patchDocument = [{ op: 'replace', path: '/isDeleted', value: false }];
      await Promise.all(ids.map(id => api.patch('task', id, patchDocument)));
      setTimeout(() => {
        toast.update(toastId, {
          render: message,
          autoClose: true,
          closeOnClick: true,
          closeButton: true,
          theme: 'task',
          isLoading: false,
        });
        toast.done(toastId);
      });
      await dispatch('fetchTasks', { page: 1, pageSize: 10000, order: 1, search: '', isDeleted: true });
      dispatch('fetchTaskCount');
      // Restoring the task un-hides the ongoing it was the last member of.
      dispatch('ongoing/syncAfterEntityChange', null, { root: true });
      return true;
    } catch (error) {
      console.error('Error restoring tasks:', error);
      return false;
    }
  },
  async deleteForever({ dispatch }, ids) {
    const count = ids.length;
    const message = count === 1 ? 'Task permanently deleted' : `${count} tasks permanently deleted`;
    const toastId = toast.loading('Please wait...', { theme: 'task' });
    try {
      const response = await api.deleteWithRequest('task/deleteForever', { ids });
      if (response.data) {
        setTimeout(() => {
          toast.update(toastId, {
            render: message,
            autoClose: true,
            closeOnClick: true,
            closeButton: true,
            theme: 'task',
            isLoading: false,
          });
          toast.done(toastId);
        });
        await dispatch('fetchTasks', { page: 1, pageSize: 10000, order: 1, search: '', isDeleted: true });
        dispatch('fetchTaskCount');
        return true;
      }
      return false;
    } catch (error) {
      console.error('Error permanently deleting tasks:', error);
      return false;
    }
  },
  async editTask({ commit, dispatch }, patchModel) {
    const toastId = toast.loading(
      'Please wait...',
      {
        theme: 'task',
      }
    );
    return await api.patch(`task`, patchModel[0], patchModel[1])
      .then((response) => {
        if (response) {
          if (response.data.timelineId) {
            dispatch('timeline/editTaskTimeline', response.data, { root: true });
            dispatch('sidepanel/editTaskTimeline', response.data, { root: true });
          }
          // commit("REMOVE_TASK", [response.data.id]);
          // commit("ADD_TASK", response.data);
          dispatch('fetchTasks', {
            page: 1,
            pageSize: 10000,
            order: 1,
            search: ''
          });
          setTimeout(() => {
            toast.update(toastId, {
              render: "Task updated",
              autoClose: true,
              closeOnClick: true,
              closeButton: true,
              theme: 'task',
              isLoading: false,
            });
            toast.done(toastId);
          });
          return true;
        }
        return false;
      });
  }
}
function calculateForMarkAsImportant(state, request) {
  const tasks = state.tasks.items.filter(item => request.includes(item.id) && !item.isImportant);
  let ids = tasks.map(i => i.id);
  return { ids };
}
function calculateForMarkAsNotImportant(state, request) {
  const tasks = state.tasks.items.filter(item => request.includes(item.id) && item.isImportant);
  let ids = tasks.map(i => i.id);
  return { ids };
}
export const mutations = {
  SET_LOADING(s, val) { s.isLoading = !!val },

  SET_TASKS(s, tasks) {
    s.tasks = tasks;
    s.isLoading = false;
  },
  ADD_TASK(s, task) {
    s.tasks.items.push(task)
    if (s.tasks.totalCount == 0) {
      s.tasks.totalCount++
      s.tasks.totalPages++
    }
  },
  SET_COUNT(s, response) {
    s.taskCounts = response
  },
  REMOVE_TASK(s, ids) {
    s.tasks.items = s.tasks.items.filter(item => !ids.includes(item.id));
  },
  UPDATE_TASK(s, response) {
    response.forEach(response => {
      const task = s.tasks.items.find(task => task.id === response.id);
      if (task) {
        task.isImportant = response.isImportant;
        task.status = response.status
        task.dueDate = response.dueDate;
        task.content = response.content
        task.title = response.title
      }
    });
  }
}
export default {
  state,
  getters,
  actions,
  mutations
};